import React, {useEffect, useState, useRef, useMemo} from "react";
import {ErrorBoundary} from "react-error-boundary";
import useSWRInfinite from "swr/infinite";
import {useStatefulRef} from "../../hooks/useStatefulRef.js";
import {
  _getKey,
  FilterTypes,
  fetcher,
  defaultValuesForFilters,
  filtersToUrl,
} from "./utils.js";
import Portal from "./Portal.jsx";
import ResultList from "./ResultList/index.jsx";
import CommitteeKeyMembers from "./CommitteeKeyMembers.jsx";
import ResultError from "./ResultList/ResultError.jsx";

export default function DrupalView({
                                     title,
                                     type,
                                     termTid,
                                     sectionId,
                                     sessionTypeId,
                                     filterOptions
                                   }) {
  // Used for portals
  const viewRef = useStatefulRef();
  const topBar = useRef();
  const resultsArea = useRef();
  const resultsAreaLoadMore = useRef();
  const sidebarArea = useRef();
  let years = useRef();
  let tracks = useRef();
  let days = useRef();
  let PAGE_MAX = useRef();

  const safelyGetFilters = (viewFilters) => {
    try {
      return JSON.parse(viewFilters);
    }
    catch (err) {
      return {};
    }
  };
  let queryParams = new URLSearchParams(window.location.search);
  let paramsObject = safelyGetFilters(queryParams.get('viewFilters')) ?? {};

// for external paging (not inifinite)
// Check if the URL is /nada/regulatory-affairs and set PAGE_MAX accordingly
  PAGE_MAX = paramsObject.page_max ?? 10;
  const isFirstRun = useRef(true);

  const [filterValues, _setFilterValues] = useState(() =>
    defaultValuesForFilters(filterOptions)
  );

  const _type = useMemo(() => type.replace(/_/g, "-"), [type]);
  const getKey = (idx) => {
    let endpoint = `/nada-content-api/${_type}?page_max=${PAGE_MAX}&page_offset=${
      idx * PAGE_MAX
    }${termTid ? `&tid=${termTid}` : ""}${sectionId ? `&section_id=${sectionId}` : ""}${sessionTypeId ? `&session_type_id=${sessionTypeId}` : ""}`;
    const key = _getKey(endpoint, filterValues);
    return key;
  };

  /** !!TODO // BETA WATCH!!
   * The beta for swr allows you to turn off the 1st request
   * check that happens to see if we need to reload every prior
   * request. This is done for sites where pagination works as a cursor,
   * where page 1 could have new content causing the cursor to now point
   * to content you've already seen. This _technically_ could happen to us
   * but also might not be worth hammering our servers when content is a
   * lot more static. Just a heads up that we will be able to have
   * control over that later
   */
  const {
    data: results,
    error: resultsError,
    setSize,
    size
  } = useSWRInfinite(getKey, fetcher, {revalidateOnFocus: false});

  const showPages = (theNum) => {
    setFilterValues('page_max', theNum);
    // window.location.reload();
  };

  const setFilterValues = (id, value) => {
    _setFilterValues({...filterValues, [id]: value});
  };

  const setMultipleFilterValues = (obj) => {
    _setFilterValues({...filterValues, ...obj});
  };

  const updateUrl = () => {
    filtersToUrl(filterValues);
  };

  useEffect(() => {
    // We only want to do this _after_ we've mounted
    if (isFirstRun.current) {
      isFirstRun.current = false;
    }
    else {
      updateUrl();
    }
  }, [filterValues]);

  // get all the props these filters will actually need
  const getFilterProps = (filter) => {
    const {id, format, options, label} = filter;
    let props = {key: id, label, id};

    switch (format) {
      case "state_country_select":
        return {
          ...props,
          defaultState: filter.states.defaultState,
          onChangeState(value) {
            setFilterValues(filter.states.id, value);
          },
          stateItems: filter.states.options,
          stateValue: filterValues[filter.states.id],
          countryItems: filter.countries.options,
          countryValue: filterValues[filter.countries.id],
          onChangeCountry(value) {
            // setFilterValues(filter.countries.id, { id: 5, name: "hello" });
            setMultipleFilterValues({
              [filter.countries.id]: value,
              [filter.states.id]: filter.states.defaultState,
            });
            // setFilterValues(filter.countries.id, value);
            // setFilterValues(filter.states.id, filter.states.defaultState);
          },
        };
      case "textfield":
        return {
          ...props,
          value: filterValues[id],
          onChange(value) {
            // this check prevents the first debounce
            // where it doesn't make sense, haha
            if (filterValues[id] !== value) {
              setFilterValues(id, value);
            }
          },
        };
      case "select":
        return {
          ...props,
          selected: filterValues[id],
          onChange(value) {
            setFilterValues(id, value);
          },
          items: options,
        };
      case "bool":
        return {
          ...props,
          checked: filterValues[id],
          onChange(e) {
            setFilterValues(id, e.target.checked);
          },
        };
      case "checkboxes":
        return {
          ...props,
          selected: filterValues[id],
          onChange(value) {
            const includesToggle = filterValues[id].includes(value);
            if (!includesToggle) {
              setFilterValues(id, [...filterValues[id], value]);
            }
            else {
              setFilterValues(
                id,
                filterValues[id].filter((v) => v !== value)
              );
            }
          },
          items: options,
        };
    }

    return props;
  };

  useEffect(() => {
    if (viewRef.current) {
      if (!resultsArea.current) {
        topBar.current = viewRef.current
          .closest(".reactive-drupal-view")
          .querySelector(".filter-top-bar");
        resultsArea.current = viewRef.current
          .closest(".reactive-drupal-view")
          .querySelector(".results-area");
        resultsAreaLoadMore.current = viewRef.current
          .closest(".reactive-drupal-view")
          .querySelector(".results-area-load-more");
        sidebarArea.current = viewRef.current
          .closest(".reactive-drupal-view")
          .querySelector(".filter-sidebar");
      }
    }
  }, [viewRef.current]);

  const topFilters = filterOptions.filter(
    (filter) =>
      ["textfield", "select", "bool", "state_country_select"].indexOf(
        filter.format
      ) >= 0
  );
  const sideFilters = filterOptions.filter(
    (filter) => ["checkboxes"].indexOf(filter.format) >= 0
  );

  const isLoadingInitialData = !results && !resultsError;
  const isLoadingMore =
    isLoadingInitialData ||
    (size > 0 && results && typeof results[size - 1] === "undefined");
  const canLoadMore =
    size + 1 <= Math.ceil(parseInt(results?.[0]?.data?.count, 10) / PAGE_MAX);

  let resultsItems =
    results?.reduce((acc, curr) => {
      years = curr?.data?.years;
      tracks = curr?.data?.tracks;
      days = curr?.data?.days;
      let nodes = curr?.data?.nodes ? curr.data.nodes : [];
      return [...acc, ...nodes];
    }, []) || [];

  // Adds up to 10 skeleton loaders at this level
  // so that the results page doesn't really worry
  // about determing if they're needed
  if (isLoadingMore) {
    const numberLeftToLoad = Math.min(
      results?.[0]?.data?.count - resultsItems.length || 10,
      PAGE_MAX
    );
    resultsItems = [
      ...resultsItems,
      ...Array(numberLeftToLoad)
        .fill()
        .map(() => ({skeleton: true})),
    ];
  }

  return (
    <div ref={viewRef}>
      <Portal to={topBar}>
        {topFilters.length > 0 ? (
          <div className="py-4 md:py-8">
            <div
              className="nada-content-list--filters max-w-6xl mx-auto search single-page-search py-2 border-b border-gray-700 w-full">
              <div
                className={`drupal-view-filters flex flex-col space-y-4 md:space-y-0 md:flex-row md:space-x-2`}
              >
                {topFilters.map((filter, index) => {
                  const Component = FilterTypes[filter.format];
                  let props = getFilterProps(filter);
                  // Filter out all but available years
                  if (props.id === 'year' && props.items.length > 0 && years.length > 0) {
                    let filteredItems = [];
                    for (let [index, item] of props.items.entries()) {
                      if (item.name !== 'All Years') {
                        let foundIt = years.includes(item.name.toString());
                        if (foundIt === true) {
                          filteredItems.push(item);
                        }
                      }
                    }
                    if (filteredItems.length > 0) {
                      props.items = filteredItems;
                    }
                  }
                  // Filter out all but available tracks
                  if (props.id === 'session_track' && props.items.length > 0 && tracks.length > 0) {
                    let filteredItems = [];
                    for (let [index, item] of props.items.entries()) {
                      if (item.name !== 'All Tracks') {
                        let foundIt = tracks.includes(item.id.toString());
                        if (foundIt === true) {
                          filteredItems.push(item);
                        }
                      }
                    }
                    if (filteredItems.length > 0) {
                      props.items = filteredItems;
                      props.items.unshift({
                        "id": "",
                        "name": "All Tracks",
                        "default": true
                      });
                    }
                  }
                  return Component ? <Component {...props} /> : null;
                })}
              </div>
            </div>
          </div>
        ) : null}
      </Portal>
      <Portal to={sidebarArea}>
        <div>

          {
            PAGE_MAX !== 10000 ? (
              <span>Showing pages of {PAGE_MAX}.
      <button
        className="showAll pb-4 mb-4 cursor-pointer font-bold text-primary text-left"
        onClick={() => showPages(10000)}>(show all results)</button>
    </span>
            ) : (
              <span>
      Showing all results. <button
                className="showPaged pb-4 mb-4 cursor-pointer font-bold text-primary text-left"
                onClick={() => showPages(10)}>(show in pages of 10)</button>
    </span>
            )
          }



          {sideFilters.length > 0 ? (
            <div
              className="flex flex-col bg-gray-200 rounded-2xl p-6 space-y-4">
              <span className="text-xl text-primary">Filters</span>
              {sideFilters.map((filter) => {
                const Component = FilterTypes[filter.format];
                const props = getFilterProps(filter);

                // Filter out all but available days
                if (props.id === 'session_date' && days.length > 0) {
                  props.items = days;
                }
                if (props.items?.length > 1) {
                  return Component ? <Component {...props} /> : null;
                }
              })}
            </div>
          ) : null}
          {type === "committee_membership" && <CommitteeKeyMembers/>}
        </div>
      </Portal>


      <ErrorBoundary FallbackComponent={ResultError}>
        <ResultList
          title={title}
          error={resultsError}
          loading={isLoadingMore || isLoadingInitialData}
          initialLoad={isLoadingInitialData}
          items={resultsItems}
          type={type}
        />
      </ErrorBoundary>
      <Portal to={resultsAreaLoadMore}>
        {canLoadMore || isLoadingMore ? (
          <button
            disabled={isLoadingMore}
            className={`w-full uppercase py-4 text-center mb-4 ${
              isLoadingMore ? "cursor-not-allowed" : "cursor-pointer"
            } border-solid border-l-0 border-r-0 border-t border-b font-bold border-gray-400 text-primary`}
            onClick={() => setSize(size + 1)}
          >
						<span className="pb-1 border-b-1 border-primary">
							{isLoadingMore ? "Loading" : "Load"} more results
						</span>
          </button>
        ) : null}
      </Portal>
    </div>
  );
}
